/* Sakal global search — the ONE predictive search implementation for the theme.
 * Markup: snippets/sakal-search-overlay.liquid (rendered once by the header section).
 * Triggers: any [data-sk-search-open] (snippets/sakal-search-trigger.liquid).
 * Results: Shopify predictive search, server-rendered by sections/sakal-predictive-search.liquid
 *          at the locale-aware route  {root}search/suggest?q=…&section_id=sakal-predictive-search
 * Guarantees: debounced (220 ms), one request per settled query, in-flight requests aborted,
 * stale responses ignored, results cached per query, listeners scoped to an AbortController and
 * torn down on Theme Editor section unload (no duplicate bindings after reloads).
 */
(() => {
  if (window.SakalSearch) return;
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const DEBOUNCE = 220;
  const MIN_CHARS = 2;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let active = null; // the single live instance

  function create(el) {
    const ac = new AbortController();
    const on = (t, ev, fn, opt) => t.addEventListener(ev, fn, Object.assign({ signal: ac.signal }, opt || {}));
    const input = el.querySelector('#SkSearchInput');
    const results = el.querySelector('[data-sk-search-results]');
    const idle = el.querySelector('[data-sk-search-idle]');
    const clearBtn = el.querySelector('[data-sk-search-clear]');
    const live = el.querySelector('[data-sk-search-live]');
    const panel = el.querySelector('.sk-so__panel');
    const cache = new Map();
    let req = null, timer = 0, seq = 0, activeIdx = -1, lastTrigger = null, shownQuery = '';

    const options = () => Array.from(results.querySelectorAll('[role="option"]'));
    const setActive = (i) => {
      const o = options();
      if (!o.length) return;
      activeIdx = (i + o.length) % o.length;
      o.forEach((x, n) => x.setAttribute('aria-selected', String(n === activeIdx)));
      input.setAttribute('aria-activedescendant', o[activeIdx].id);
      o[activeIdx].scrollIntoView({ block: 'nearest' });
    };
    const resetActive = () => { activeIdx = -1; input.removeAttribute('aria-activedescendant'); };
    const showIdle = (yes) => { idle.hidden = !yes; results.hidden = yes; input.setAttribute('aria-expanded', String(!yes)); };

    function render(html, q) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-sk-predictive]');
      results.innerHTML = content ? content.innerHTML : '';
      shownQuery = q;
      resetActive();
      showIdle(false);
      if (live && content) live.textContent = content.dataset.summary || '';
    }
    function status(text, spinner) {
      results.innerHTML = `<div class="sk-so__status">${spinner ? '<span class="sk-spinner" aria-hidden="true"></span>' : ''}<span></span></div>`;
      results.querySelector('.sk-so__status span:last-child').textContent = text;
      showIdle(false);
    }

    async function fetchResults(q) {
      if (cache.has(q)) return render(cache.get(q), q);
      if (req) req.abort();
      const ctrl = (req = new AbortController());
      // A request that never answers ends like a failed one (not like a cancelled one), so "Searching…" can't stick
      const limit = setTimeout(() => ctrl.abort(new DOMException('Search timed out', 'TimeoutError')), 15000);
      const mine = ++seq;
      results.setAttribute('aria-busy', 'true');
      if (shownQuery === '') status('Searching…', true);
      else results.classList.add('is-updating');
      try {
        const params = new URLSearchParams();
        params.set('q', q);
        params.set('section_id', 'sakal-predictive-search');
        params.set('resources[type]', 'query,product,collection,article,page');
        params.set('resources[limit]', '6');
        params.set('resources[limit_scope]', 'each');
        params.set('resources[options][unavailable_products]', 'last');
        params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,body');
        const res = await fetch(`${ROOT}search/suggest?${params.toString()}`, { signal: req.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw Object.assign(new Error('HTTP ' + res.status), { status: res.status });
        const html = await res.text();
        if (mine !== seq || q !== input.value.trim()) return; // stale response — a newer query is in flight or shown
        cache.set(q, html);
        render(html, q);
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (mine !== seq) return;
        results.innerHTML = '';
        const box = document.createElement('div');
        box.className = 'sk-so__status';
        box.innerHTML = '<span>Suggestions are unavailable right now.</span> ';
        const a = document.createElement('a');
        a.className = 'sk-link';
        a.href = `${ROOT}search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
        a.textContent = `See all results for “${q}”`;
        box.appendChild(a);
        results.appendChild(box);
        showIdle(false);
      } finally {
        clearTimeout(limit);
        if (mine === seq) { results.setAttribute('aria-busy', 'false'); results.classList.remove('is-updating'); }
      }
    }

    function onInput() {
      const q = input.value.trim();
      clearBtn.hidden = !input.value;
      clearTimeout(timer);
      if (q.length < MIN_CHARS) {
        if (req) req.abort();
        seq++;
        shownQuery = '';
        results.innerHTML = '';
        showIdle(true);
        resetActive();
        return;
      }
      if (q === shownQuery) return;
      timer = setTimeout(() => fetchResults(q), DEBOUNCE);
    }

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      const f = Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([type=hidden])')).filter((x) => x.offsetParent !== null && !x.closest('[hidden]'));
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    // While the box is empty, its placeholder types "Search books", "Search authors"… (static under reduced motion)
    const words = (input.dataset.skRollWords || '').split('|').map((w) => w.trim()).filter(Boolean);
    const basePh = input.placeholder;
    let stopType = null;
    const stopPh = () => { if (stopType) stopType(); stopType = null; input.placeholder = basePh; };
    const startPh = () => {
      if (words.length < 2 || reduceMotion.matches || stopType) return;
      input.placeholder = `Search ${words[0]}`;
      stopType = typewriter(words, (txt) => { input.placeholder = `Search ${txt}`; }, () => !!input.value);
    };

    function open(trigger) {
      if (!el.hidden) { input.focus(); return; }
      startPh();
      lastTrigger = trigger || document.activeElement;
      el.hidden = false;
      document.documentElement.classList.add('sk-search-open');
      document.querySelectorAll('[data-sk-search-open]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => el.classList.add('is-open'));
      input.focus({ preventScroll: true });
      if (input.value.trim().length >= MIN_CHARS) onInput(); else showIdle(true);
      document.dispatchEvent(new CustomEvent('sakal:search:open'));
    }
    function close() {
      if (el.hidden) return;
      stopPh();
      el.classList.remove('is-open');
      document.documentElement.classList.remove('sk-search-open');
      document.querySelectorAll('[data-sk-search-open]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (req) req.abort();
      clearTimeout(timer);
      const done = () => { el.hidden = true; };
      if (reduceMotion.matches) done(); else setTimeout(done, 200);
      resetActive();
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
    }

    on(input, 'input', onInput);
    on(input, 'keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIdx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIdx - 1); }
      else if (e.key === 'Enter' && activeIdx > -1) {
        const o = options()[activeIdx];
        if (o && o.href) {
          e.preventDefault();
          if (window.Sakal && Sakal.Loader) Sakal.Loader.navigating(); // brand loader where the browser can't report it
          window.location.href = o.href;
        }
      } else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
    on(el, 'keydown', (e) => { if (e.key === 'Escape' && e.target !== input) { e.preventDefault(); close(); } trapFocus(e); });
    on(el, 'click', (e) => {
      if (e.target.closest('[data-sk-search-close]')) { e.preventDefault(); close(); return; }
      const term = e.target.closest('[data-sk-search-term]');
      if (term) { e.preventDefault(); input.value = term.dataset.skSearchTerm; onInput(); input.focus(); }
    });
    on(clearBtn, 'click', () => { input.value = ''; onInput(); input.focus(); });
    on(el.querySelector('form'), 'submit', (e) => { if (!input.value.trim()) { e.preventDefault(); input.focus(); } });

    return { el, open, close, destroy() { ac.abort(); if (req) req.abort(); clearTimeout(timer); stopPh(); document.documentElement.classList.remove('sk-search-open'); } };
  }

  // Typewriter: shows a word, erases it letter by letter, types the next. Letters are graphemes (Intl.Segmenter),
  // so Devanagari conjuncts such as स्त are never split. paint(text) draws; paused() holds the current state.
  function graphemes(str) {
    try { return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(str), (g) => g.segment); }
    catch (_) { return Array.from(str); }
  }
  function typewriter(words, paint, paused) {
    const parts = words.map(graphemes);
    let wi = 0, n = parts[0].length, typing = false, t = 0, stopped = false;
    const step = () => {
      if (stopped) return;
      let delay = 400;
      if (!paused()) {
        const g = parts[wi];
        if (typing) {
          n += 1; paint(g.slice(0, n).join(''));
          if (n >= g.length) { typing = false; delay = 1600; } else delay = 95;
        } else {
          n -= 1; paint(g.slice(0, Math.max(n, 0)).join(''));
          if (n <= 0) { n = 0; wi = (wi + 1) % parts.length; typing = true; delay = 300; } else delay = 45;
        }
      }
      t = setTimeout(step, delay);
    };
    t = setTimeout(step, 1600);
    return () => { stopped = true; clearTimeout(t); };
  }

  // Search bars ([data-sk-roll], snippets/sakal-search-trigger.liquid): one shared typewriter for every bar on the
  // page, painting whatever bars exist at each step so re-rendered headers keep working. Holds while the tab is
  // hidden or search is open; never started under reduced motion (the first word simply stays).
  let barTyping = false;
  function startRoll() {
    const first = document.querySelector('[data-sk-roll]');
    if (barTyping || !first || reduceMotion.matches) return;
    const words = first.dataset.skRoll.split('|').map((w) => w.trim()).filter(Boolean);
    if (words.length < 2) return;
    barTyping = true;
    typewriter(words, (txt) => document.querySelectorAll('[data-sk-roll] .sk-strig__typed').forEach((el) => { el.textContent = txt; }),
      () => document.hidden || document.documentElement.classList.contains('sk-search-open'));
  }

  function init(scope) {
    const el = (scope || document).querySelector('[data-sk-search]');
    if (!el || (active && active.el === el)) return;
    if (active) active.destroy();
    active = create(el);
  }

  // Delegated, bound once for the whole page lifetime.
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-sk-search-open]');
    if (!t || !active) return;
    e.preventDefault();
    active.open(t);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || !active || e.metaKey || e.ctrlKey || e.altKey) return;
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
    e.preventDefault();
    active.open();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (active && e.target.contains(active.el)) { active.destroy(); active = null; }
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startRoll); else startRoll();
  document.addEventListener('shopify:section:load', startRoll);
  window.SakalSearch = { init, open: (t) => active && active.open(t), close: () => active && active.close(), get instance() { return active; } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
})();
